// ⚠️ AFFILIATE LINK PLACEHOLDERS
// Replace these with your real affiliate tracking URLs as you sign up with each provider.
export const VIRTUAL_ADDRESS_PROVIDERS = [
  { name: "iPostal1",                price: "$9.99/mo",  best_for: "Cheapest virtual mailbox + scanning", url: "https://ipostal1.com" },
  { name: "Anytime Mailbox",         price: "$9.99/mo",  best_for: "Most US locations to choose from",   url: "https://anytimemailbox.com" },
  { name: "Snapmailbox",             price: "$5/mo",     best_for: "Solo entrepreneurs on a budget",     url: "https://snapmailbox.com" },
  { name: "Alliance Virtual Offices", price: "$49/mo",   best_for: "Premium prestigious addresses",      url: "https://www.alliancevirtualoffices.com" },
  { name: "Regus",                   price: "$59+/mo",   best_for: "Co-working + meeting rooms included", url: "https://www.regus.com" },
];

export const BUSINESS_PHONE_PROVIDERS = [
  { name: "Google Voice",  price: "Free (or $10/mo Workspace)", best_for: "Cheapest, works with Gmail",       url: "https://voice.google.com" },
  { name: "OpenPhone",     price: "$15/mo",                     best_for: "Modern UI, SMS, team-friendly",    url: "https://www.openphone.com" },
  { name: "Phone.com",     price: "$13.49/mo",                  best_for: "Traditional business phone, IVR",  url: "https://www.phone.com" },
  { name: "Grasshopper",   price: "$26/mo",                     best_for: "Solo entrepreneurs, easy setup",   url: "https://grasshopper.com" },
  { name: "RingCentral",   price: "$30/mo",                     best_for: "Larger teams + video meetings",    url: "https://www.ringcentral.com" },
];

// ⚠️ AFFILIATE LINK — Paste your full NerdWallet affiliate/partner URL here once you have it.
// Sign up at https://www.nerdwallet.com/partners or via Impact.com to get your tracking link.
export const NERDWALLET_AFFILIATE_URL =
  "https://www.nerdwallet.com/best/small-business/business-checking-accounts";

export const chapters = [
  {
    id: 0,
    title: "Prerequisites — Business Address & Phone",
    subtitle: "Set these up before you file your LLC",
    icon: "ListChecks",
    isPrerequisite: true,
    checklistItems: [
      "Business mailing address ready",
      "Business phone number ready",
    ],
    content: {
      intro: "Before you file your LLC, you need two essentials: a business mailing address and a business phone number. These appear on your formation paperwork, your EIN application, and your bank-account paperwork — so getting them set up first will save you headaches later.",
      sections: [
        {
          heading: "Why this matters",
          items: [
            "Required to file your LLC formation paperwork",
            "Required to apply for an EIN with the IRS",
            "Required to open a business bank account",
            "Required for business credit applications",
            "Required for a Google Business Profile",
            "Keeps your personal home address and cell number private",
          ],
        },
      ],
      proTip: "Don't use your home address or personal cell phone on your LLC filing — it becomes public record. A virtual mailbox or business phone number is cheap insurance for your privacy.",
    },
    links: [],
  },
  {
    id: 1,
    title: "Choose a Unique LLC Name",
    subtitle: "Your first step to building a brand",
    icon: "FileText",
    checklistItems: [
      "Search the Texas SOS database (SOSDirect)",
      "Verify name includes 'LLC' or 'Limited Liability Company'",
      "Check for restricted words (bank, trust, insurance)",
      "Search domain name availability",
      "Check social media handle availability",
    ],
    content: {
      intro: "Your LLC name is more than just a label — it's the foundation of your brand. Texas requires your name to be distinguishable from any existing business registered in the state.",
      sections: [
        {
          heading: "Requirements",
          items: [
            "Must include 'LLC,' 'L.L.C.,' or 'Limited Liability Company'",
            "Must be distinguishable from existing Texas businesses",
            "Cannot include restricted words (bank, trust, insurance) without proper approval",
          ],
        },
        {
          heading: "How to Check Availability",
          items: [
            "Search the Texas Secretary of State database via SOSDirect",
            "Do a basic Google search and domain check for branding consistency",
            "Check social media platforms for handle availability",
          ],
        },
      ],
      proTip: "Secure your domain name and social media handles immediately after confirming your LLC name is available. Don't wait — good names get taken fast!",
    },
    links: [
      { label: "Search Texas SOS Database (SOSDirect)", url: "https://www.sos.state.tx.us/corp/sosda/index.shtml" },
      { label: "Search Business Names (Texas Comptroller)", url: "https://comptroller.texas.gov/taxes/franchise/account-status/search" },
      { label: "GoDaddy Domain Search", url: "https://www.godaddy.com/domains" },
    ],
  },
  {
    id: 2,
    title: "Appoint a Registered Agent",
    subtitle: "Your LLC's official point of contact",
    icon: "UserCheck",
    checklistItems: [
      "Decide who will serve as registered agent",
      "Confirm physical Texas address (no P.O. boxes)",
      "Ensure availability during business hours",
    ],
    content: {
      intro: "Every Texas LLC must have a registered agent — a person or service designated to receive legal and official documents on behalf of your business.",
      sections: [
        {
          heading: "Your Options",
          items: [
            "Yourself — if you have a Texas address and are available during business hours",
            "A trusted friend or family member who meets the requirements",
            "A professional registered agent service (recommended for privacy and reliability)",
          ],
        },
        {
          heading: "Requirements",
          items: [
            "Must have a physical street address in Texas (no P.O. boxes)",
            "Must be available during standard business hours to receive documents",
          ],
        },
      ],
      proTip: "Using a professional registered agent service keeps your home address off public records and ensures you never miss critical legal notices. Missing them can lead to penalties or even loss of LLC status.",
    },
    links: [
      { label: "Texas SOS — Registered Agent Info", url: "https://www.sos.state.tx.us/corp/registeredagents.shtml" },
    ],
  },
  {
    id: 3,
    title: "File Certificate of Formation (Form 205)",
    subtitle: "Officially create your LLC with Texas",
    icon: "FilePlus",
    checklistItems: [
      "Prepare LLC name, agent info, and management structure",
      "File online via SOSDirect or by mail",
      "Pay the $300 state filing fee",
      "Double-check all information before submitting",
    ],
    content: {
      intro: "The Certificate of Formation (Form 205) is the official document that legally creates your LLC in Texas. This is the most important filing you'll make.",
      sections: [
        {
          heading: "What's Included",
          items: [
            "Your LLC name",
            "Registered agent name and address",
            "Management structure — member-managed or manager-managed",
            "Organizer details (the person filing the form)",
          ],
        },
        {
          heading: "How to File",
          items: [
            "Online via SOSDirect — fastest option (2-5 business days processing)",
            "By mail — slower processing time",
          ],
        },
        {
          heading: "Cost & Timing",
          items: [
            "Filing fee: $300 (non-refundable)",
            "Online processing: 2-5 business days",
            "Mail processing: typically longer",
          ],
        },
      ],
      proTip: "Double-check every detail before submitting. Errors can cause delays or rejection, and the filing fee is non-refundable.",
    },
    links: [
      { label: "File Online via SOSDirect", url: "https://www.sos.state.tx.us/corp/sosda/index.shtml" },
      { label: "Form 205 (PDF Download) — Only if registering by mail", url: "https://www.sos.state.tx.us/corp/forms/205_boc.pdf" },
    ],
  },
  {
    id: 4,
    title: "Create an Operating Agreement",
    subtitle: "The rulebook for your LLC",
    icon: "BookOpen",
    checklistItems: [
      "Draft ownership percentages and roles",
      "Define profit distribution rules",
      "Outline decision-making processes",
      "Include member departure procedures",
    ],
    content: {
      intro: "While Texas doesn't legally require an Operating Agreement, it's one of the most important documents you'll create. Think of it as the internal rulebook for how your LLC operates.",
      sections: [
        {
          heading: "What It Covers",
          items: [
            "Ownership percentages for each member",
            "Roles and responsibilities",
            "How profits and losses are distributed",
            "Decision-making processes and voting rights",
            "What happens if a member leaves or passes away",
          ],
        },
        {
          heading: "Why It Matters",
          items: [
            "Prevents disputes between members",
            "Strengthens your liability protection in court",
            "Shows professionalism to banks, investors, and partners",
          ],
        },
      ],
      proTip: "Even single-member LLCs should have an Operating Agreement. It reinforces the separation between you and your business, which is key to maintaining liability protection.",
    },
    links: [
      { label: "Texas BOC — Operating Agreement Reference", url: "https://statutes.capitol.texas.gov/Docs/BO/htm/BO.101.htm" },
    ],
  },
  {
    id: 5,
    title: "Get an EIN from the IRS",
    subtitle: "Your business's Social Security number",
    icon: "Hash",
    checklistItems: [
      "Apply online through the IRS website",
      "Receive your EIN immediately upon completion",
      "Save your EIN confirmation letter",
    ],
    content: {
      intro: "An Employer Identification Number (EIN) is like a Social Security number for your business. It's free and takes about 10 minutes to get online.",
      sections: [
        {
          heading: "You'll Need It To",
          items: [
            "Open a business bank account",
            "Hire employees",
            "File federal and state taxes",
          ],
        },
        {
          heading: "How to Apply",
          items: [
            "Apply online through the IRS website — completely free",
            "Takes approximately 10 minutes",
            "You'll receive your EIN immediately after completing the application",
          ],
        },
      ],
      proTip: "Apply for your EIN immediately after your LLC is approved. You'll need it for almost every next step, especially opening a bank account. Avoid third-party services that charge for this — it's always free directly from the IRS.",
    },
    links: [
      { label: "Apply for EIN Online (IRS)", url: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
    ],
  },
  {
    id: 6,
    title: "Open a Business Bank Account",
    subtitle: "Separate personal and business finances",
    icon: "Landmark",
    checklistItems: [
      "Gather required documents (EIN, Certificate of Formation, Operating Agreement, ID)",
      "Research banks with small-business support",
      "Open a dedicated business checking account",
    ],
    content: {
      intro: "Separating your personal and business finances is critical. It protects your personal assets, simplifies bookkeeping, and builds credibility.",
      sections: [
        {
          heading: "What You'll Need",
          items: [
            "Your EIN (Employer Identification Number)",
            "Certificate of Formation",
            "Operating Agreement",
            "Valid government-issued ID",
          ],
        },
        {
          heading: "Why It Matters",
          items: [
            "Protects your personal assets from business liabilities",
            "Simplifies bookkeeping and tax preparation",
            "Builds business credibility with clients and vendors",
          ],
        },
      ],
      proTip: "Choose a bank with low fees and strong small-business support. Many banks offer free business checking with a minimum balance. Ask about fee waivers and online banking features.",
    },
    links: [
      { label: "NerdWallet — Best Business Bank Accounts", url: "https://www.nerdwallet.com/best/small-business/business-checking-accounts" },
      { label: "Compare Business Bank Accounts (NerdWallet) — Partner Link", url: NERDWALLET_AFFILIATE_URL },
    ],
  },
  {
    id: 7,
    title: "Understand Texas Franchise Tax",
    subtitle: "Yes, you still have to file — even if you owe $0",
    icon: "Receipt",
    checklistItems: [
      "Understand the franchise tax threshold",
      "Set a reminder for May 15 annual filing",
      "Know what forms to file (Franchise Tax Report + PIR)",
    ],
    content: {
      intro: "Texas doesn't have a state income tax for LLCs, but it does have a franchise tax. The good news? Most small businesses owe $0 — but you still must file annually.",
      sections: [
        {
          heading: "Key Points",
          items: [
            "Most small businesses owe $0 in franchise tax, but filing is still mandatory",
            "Annual report is due May 15 each year",
            "Tax is based on revenue thresholds set by the state",
          ],
        },
        {
          heading: "What to File",
          items: [
            "Franchise Tax Report",
            "Public Information Report (PIR)",
          ],
        },
      ],
      proTip: "Set a yearly calendar reminder for May 15. Failing to file — even if you owe nothing — can result in penalties and eventually force the state to forfeit your LLC.",
    },
    links: [
      { label: "Texas Comptroller — Franchise Tax", url: "https://comptroller.texas.gov/taxes/franchise/" },
      { label: "File Franchise Tax Online", url: "https://comptroller.texas.gov/taxes/franchise/filing-requirements.php" },
    ],
  },
  {
    id: 8,
    title: "Get Licenses and Permits",
    subtitle: "Make sure you're legally cleared to operate",
    icon: "Shield",
    checklistItems: [
      "Check if you need a sales tax permit",
      "Research industry-specific licenses",
      "Check local city and county permit requirements",
    ],
    content: {
      intro: "Depending on your business type and location, you may need additional licenses or permits beyond your LLC formation. It's important to research this before you start operating.",
      sections: [
        {
          heading: "Common Examples",
          items: [
            "Sales tax permit (if selling taxable goods or services)",
            "Professional licenses (for regulated industries)",
            "Local city or county business permits",
          ],
        },
        {
          heading: "Where to Check",
          items: [
            "Texas Department of Licensing & Regulation (TDLR)",
            "Your local city or county clerk's office",
            "Industry-specific regulatory bodies",
          ],
        },
      ],
      proTip: "Always verify licensing requirements before launching your business. Operating without required permits can result in fines or forced closure.",
    },
    links: [
      { label: "Texas TDLR — Licensing Info", url: "https://www.tdlr.texas.gov/" },
      { label: "Texas Comptroller — Sales Tax Permit", url: "https://comptroller.texas.gov/taxes/sales/" },
    ],
  },
  {
    id: 9,
    title: "Stay Compliant with Annual Filings",
    subtitle: "Keep your LLC in good standing",
    icon: "CalendarCheck",
    checklistItems: [
      "File franchise tax reports annually by May 15",
      "Maintain your registered agent",
      "Update the state if business info changes",
      "Renew licenses and permits on time",
      "Document major business decisions",
    ],
    content: {
      intro: "Forming your LLC is just the beginning. Staying compliant ensures your business remains in good standing and your liability protection stays intact.",
      sections: [
        {
          heading: "Ongoing Responsibilities",
          items: [
            "File franchise tax reports annually (due May 15)",
            "Maintain an active registered agent at all times",
            "Update the Texas Secretary of State if your business info changes",
            "Renew all licenses and permits on time",
            "Document major business decisions in writing",
          ],
        },
        {
          heading: "Good Habits",
          items: [
            "Keep organized financial records",
            "Use accounting software from the start",
            "Schedule quarterly reviews of your compliance obligations",
          ],
        },
      ],
      proTip: "Consider using a compliance calendar service or set recurring reminders. Many LLC owners lose their good standing simply because they forgot a filing deadline.",
    },
    links: [
      { label: "Texas SOS — Maintain Your Entity", url: "https://www.sos.state.tx.us/corp/sosda/index.shtml" },
      { label: "Texas Comptroller — Filing Requirements", url: "https://comptroller.texas.gov/taxes/franchise/filing-requirements.php" },
    ],
  },
];

export const costEstimatorDefaults = {
  stateFilingFee: 300,
  registeredAgent: 0,
  operatingAgreement: 0,
  ein: 0,
  bankAccount: 0,
  licensesPermits: 0,
};

export const costCategories = [
  {
    key: "stateFilingFee",
    label: "State Filing Fee",
    description: "Filing the Certificate of Formation with Texas SOS",
    min: 300,
    max: 300,
    fixed: true,
  },
  {
    key: "registeredAgent",
    label: "Registered Agent",
    description: "Free if you serve yourself, or $50-$150/yr for a professional service",
    min: 0,
    max: 150,
    fixed: false,
  },
  {
    key: "operatingAgreement",
    label: "Operating Agreement",
    description: "Free with templates, or $50-$200 for professional drafting",
    min: 0,
    max: 200,
    fixed: false,
  },
  {
    key: "ein",
    label: "EIN (IRS)",
    description: "Always free directly from the IRS",
    min: 0,
    max: 0,
    fixed: true,
  },
  {
    key: "bankAccount",
    label: "Business Bank Account",
    description: "Free to $25/month depending on the bank",
    min: 0,
    max: 25,
    fixed: false,
  },
  {
    key: "licensesPermits",
    label: "Licenses & Permits",
    description: "Varies by industry — some are free, others $50-$300+",
    min: 0,
    max: 300,
    fixed: false,
  },
];
